import React, { useState } from 'react'
import { Cloud, CheckCircle, AlertCircle, Loader2, ExternalLink, Shield } from 'lucide-react'
import type { Deployment } from '../types'
import { probeUrl } from '../lib/api'

interface DeployPanelProps {
  deployments: Record<string, Deployment>
  className?: string
  onLog?: (msg: string) => void
}

const getStatusConfig = (status: string) => {
  if (status === 'deployed') return { color: 'text-[#10b981]', bg: 'bg-[#10b981]/10', border: 'border-[#10b981]/30', icon: CheckCircle, label: 'DEPLOYED' }
  if (status === 'building' || status === 'pending') return { color: 'text-[#f59e0b]', bg: 'bg-[#f59e0b]/10', border: 'border-[#f59e0b]/30', icon: Loader2, label: 'BUILDING' }
  if (status === 'failed') return { color: 'text-[#ef4444]', bg: 'bg-[#ef4444]/10', border: 'border-[#ef4444]/30', icon: AlertCircle, label: 'FAILED' }
  return { color: 'text-[#9ca3af]', bg: 'bg-[#9ca3af]/10', border: 'border-[#9ca3af]/30', icon: AlertCircle, label: status.toUpperCase() }
}

const formatTime = (ts: number) => ts ? new Date(ts).toLocaleString() : 'Never'

export function DeployPanel({ deployments, className = '', onLog }: DeployPanelProps) {
  const [busy, setBusy] = useState<string | null>(null)
  const [local, setLocal] = useState(deployments)

  React.useEffect(() => { setLocal(deployments) }, [deployments])

  const check = async (dep: Deployment) => {
    setBusy(dep.name)
    try {
      const result = await probeUrl(dep.url)
      setLocal((prev) => ({
        ...prev,
        [dep.name]: { ...dep, status: result.ok ? 'deployed' : 'failed' },
      }))
      onLog?.(`${dep.name}: ${result.status} HTTP ${result.http} ${result.ms}ms`)
    } catch (err) {
      onLog?.(err instanceof Error ? err.message : 'probe failed')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className={`${className} bg-[#111827] rounded-xl border border-[#374151] overflow-hidden flex flex-col`}>
      <div className="px-6 py-4 border-b border-[#374151] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#f59e0b] to-[#ec4899] flex items-center justify-center">
            <Cloud className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-lg font-bold">CLOUDFLARE PAGES</h2>
        </div>
        <span className="text-xs text-[#6b7280] font-mono">{Object.keys(local).length} projects</span>
      </div>

      <div className="flex-1 p-6 overflow-y-auto scrollbar-thin">
        <div className="space-y-3">
          {Object.values(local).map((dep) => {
            const config = getStatusConfig(dep.status)
            const StatusIcon = config.icon
            return (
              <div key={dep.name} className="bg-[#0a0f1a] rounded-lg border border-[#374151] overflow-hidden hover:border-[#4b5563] transition-colors">
                <div className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.bg} ${config.border}`}>
                        <StatusIcon className={`w-5 h-5 ${config.color} ${busy === dep.name ? 'animate-spin' : ''}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold font-mono text-sm">{dep.name}</h3>
                          <span className={`px-2 py-0.5 rounded text-xs font-mono ${config.bg} ${config.border} ${config.color}`}>
                            {config.label}
                          </span>
                        </div>
                        <p className="text-xs text-[#6b7280] font-mono truncate mt-1">{dep.url}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {dep.url && (
                        <a href={dep.url} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 text-xs bg-[#1f2937] hover:bg-[#374151] border border-[#374151] rounded-lg text-[#06b6d4] font-mono flex items-center gap-1 transition-colors">
                          <ExternalLink className="w-3 h-3" />
                          Open
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={() => check(dep)}
                        disabled={busy === dep.name}
                        className="px-3 py-1.5 text-xs bg-[#1f2937] hover:bg-[#374151] border border-[#374151] rounded-lg text-[#f59e0b] font-mono flex items-center gap-1 transition-colors disabled:opacity-50"
                      >
                        <Shield className="w-3 h-3" />
                        Probe
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-[#374151] grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                    <div>
                      <div className="text-[#6b7280] uppercase tracking-wider">Last Deploy</div>
                      <div className="font-mono text-[#f9fafb]">{formatTime(dep.last_deploy)}</div>
                    </div>
                    <div>
                      <div className="text-[#6b7280] uppercase tracking-wider">Target</div>
                      <div className="font-mono text-[#f9fafb] capitalize">{dep.target.replace('-', ' ')}</div>
                    </div>
                    <div>
                      <div className="text-[#6b7280] uppercase tracking-wider">Project Path</div>
                      <div className="font-mono text-[#9ca3af] truncate">{dep.project_path}</div>
                    </div>
                    <div>
                      <div className="text-[#6b7280] uppercase tracking-wider">Status</div>
                      <div className={`font-mono ${config.color}`}>{config.label}</div>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
